import { Permission } from '../dto';

// CR004 permission policy — single source of truth for how permissions are
// categorised. Mirrors docs/permission-policy.xlsx (the "Permissions" sheet).
//
//   field  — only meaningful in the MOBILE field app (driver actions)
//   office — only meaningful in the WEB admin portal
//   both   — used on both platforms
//
// Two consumers:
//   • Role configuration UI filters the permission list by category, so a
//     "Driver" role only shows field permissions (no confusing "View users").
//   • Login/visibility gates use FIELD_LOGIN_PERMISSIONS / ADMIN_PORTAL.

export type PermissionCategory = 'field' | 'office' | 'both';
export type RoleCategory = 'field' | 'office';

export const PERMISSION_META: Record<
  Permission,
  { category: PermissionCategory; module: string }
> = {
  [Permission.SUBMIT_REPORT]: { category: 'field', module: 'Collections' },
  [Permission.VIEW_SUBMITTED_REPORT]: {
    category: 'field',
    module: 'Collections',
  },
  [Permission.VIEW_BIN_LOCATIONS]: { category: 'field', module: 'Collections' },
  [Permission.REPORT_ISSUES]: { category: 'field', module: 'Collections' },
  [Permission.EDIT_REPORT]: { category: 'both', module: 'Collections' },
  [Permission.DELETE_REPORT]: { category: 'office', module: 'Collections' },
  [Permission.ASSIGN_DRIVER]: { category: 'office', module: 'Drivers' },
  [Permission.EDIT_DRIVER_PERMISSIONS]: {
    category: 'office',
    module: 'Drivers',
  },
  [Permission.VIEW_DRIVER_ACTIVITY]: { category: 'office', module: 'Drivers' },
  [Permission.DELETE_DRIVER]: { category: 'office', module: 'Drivers' },
  [Permission.VIEW_USERS]: { category: 'office', module: 'Users' },
  [Permission.CREATE_USER]: { category: 'office', module: 'Users' },
  [Permission.EDIT_USER]: { category: 'office', module: 'Users' },
  [Permission.DELETE_USER]: { category: 'office', module: 'Users' },
  [Permission.VIEW_SITES]: { category: 'office', module: 'Sites' },
  [Permission.EDIT_SITE]: { category: 'office', module: 'Sites' },
  [Permission.DELETE_SITE]: { category: 'office', module: 'Sites' },
  [Permission.CREATE_SITE]: { category: 'office', module: 'Sites' },
  [Permission.VIEW_SITES_SCHEDULE]: { category: 'office', module: 'Schedules' },
  [Permission.EDIT_SITE_SCHEDULE]: { category: 'office', module: 'Schedules' },
  [Permission.DELETE_SITE_SCHEDULE]: {
    category: 'office',
    module: 'Schedules',
  },
  [Permission.CREATE_SITE_SCHEDULE]: {
    category: 'office',
    module: 'Schedules',
  },
  [Permission.VIEW_REPORTS]: { category: 'office', module: 'Reporting' },
  [Permission.APPROVE_REPORTS]: { category: 'office', module: 'Reporting' },
  [Permission.EXPORT_COLLECTION]: { category: 'office', module: 'Reporting' },
  [Permission.VIEW_MASTER_DATA]: { category: 'office', module: 'Master Data' },
  [Permission.EDIT_MASTER_DATA]: { category: 'office', module: 'Master Data' },
  [Permission.DELETE_MASTER_DATA]: {
    category: 'office',
    module: 'Master Data',
  },
  [Permission.CREATE_MASTER_DATA]: {
    category: 'office',
    module: 'Master Data',
  },
  [Permission.MANAGE_SYSTEM_SETTINGS]: { category: 'office', module: 'System' },
  [Permission.VIEW_ANALYTICS]: { category: 'office', module: 'Reporting' },
  [Permission.ADMIN_PORTAL]: { category: 'office', module: 'System' },
};

// What a role MUST have to log into the mobile field app: the core "Record
// collections" capability (SUBMIT_REPORT, which also bundles view-own-history +
// report-failure via PERMISSION_MERGES). Other mobile capabilities (e.g. the Map
// / bin locations) are OPTIONAL and hidden per-tab in the app, not blocking login.
// Keep in sync with the mobile `hasFieldAppAccess` gate.
export const FIELD_LOGIN_PERMISSIONS: string[] = [Permission.SUBMIT_REPORT];

// Everything the "Record collections" row grants — used by the "Add required"
// fix so the role gets the whole bundle, not just SUBMIT_REPORT.
export const RECORD_COLLECTIONS_BUNDLE: string[] = [
  Permission.SUBMIT_REPORT,
  Permission.VIEW_SUBMITTED_REPORT,
  Permission.REPORT_ISSUES,
];

// All mobile-app permission values — used to detect a role is "meant for drivers"
// (has some field access) even when it can't log in yet.
export const MOBILE_PERMISSION_VALUES: string[] = [
  Permission.SUBMIT_REPORT,
  Permission.VIEW_SUBMITTED_REPORT,
  Permission.VIEW_BIN_LOCATIONS,
  Permission.REPORT_ISSUES,
];

// Friendly, domain-language labels for the role-config UI. Keyed by the enum KEY
// name (what GET /permissions returns as `key`). This is PRESENTATION ONLY — the
// stored permission VALUE is unchanged — so a non-technical admin reads plain
// language instead of raw strings. Falls back to the raw value when missing.
// `description` = the short one-liner shown under the label in the row.
// `detail`      = the fuller "business description" shown in the info-icon tooltip,
//                 so an admin can understand exactly what the permission does
//                 without anyone explaining it.
export const PERMISSION_LABELS: Record<
  string,
  { label: string; description?: string; detail?: string }
> = {
  // 📱 Mobile / field app
  SUBMIT_REPORT: {
    label: 'Record collections',
    description: 'Log successful or failed collections and view own history',
    detail:
      'The core driver ability. In the mobile app they can scan a bin/bag and log a successful or failed collection, and see their own past records. Required for a driver to log into the mobile app.',
  },
  VIEW_SUBMITTED_REPORT: {
    label: 'View own collection history',
    description: 'See collections they have submitted',
    detail:
      'Lets a driver open the Reports tab in the mobile app and review the collections they themselves have submitted.',
  },
  VIEW_BIN_LOCATIONS: {
    label: 'See bin / bag locations',
    description: 'Map of assigned sites and bins',
    detail:
      'Adds the Map tab in the mobile app, showing the sites/bins assigned to the driver so they can navigate to pickups. Optional — a driver can still record collections without it.',
  },
  REPORT_ISSUES: {
    label: 'Report a failed collection',
    description: 'Log an unsuccessful collection or issue',
    detail:
      'Lets a driver record when a collection could not be completed (blocked access, missing bin, etc.). Included in “Record collections”.',
  },
  // Shared
  EDIT_REPORT: {
    label: 'Edit a collection record',
    description: 'Change a recorded collection',
    detail:
      'Allows correcting a collection after it was submitted — for example fixing a wrong quantity or collection type.',
  },
  // 💻 Web — reporting
  VIEW_REPORTS: {
    label: 'View collection reports',
    description: 'See collections across drivers and sites',
    detail:
      'Read-only access to the Collection Report screen: every collection across all drivers and sites in this project, with history and details.',
  },
  APPROVE_REPORTS: {
    label: 'Approve reports',
    detail:
      'Lets a supervisor review and sign off submitted collection reports as approved.',
  },
  EXPORT_COLLECTION: {
    label: 'Export reports to CSV',
    description: 'Download collection data',
    detail:
      'Download collection data as a CSV/Excel file — useful for accounting, invoicing or external reporting.',
  },
  DELETE_REPORT: {
    label: 'Delete a collection record',
    detail:
      'Permanently removes a collection record. Use sparingly — for duplicates or genuine mistakes.',
  },
  VIEW_ANALYTICS: {
    label: 'View dashboard analytics',
    description: 'Summary charts and totals',
    detail:
      'Access to the Dashboard charts and summary totals (weights, counts and trends) for the project.',
  },
  // 💻 Web — users
  VIEW_USERS: {
    label: 'View users',
    detail:
      'Read-only: see the list of people in this project and the roles they hold.',
  },
  CREATE_USER: {
    label: 'Add users',
    detail:
      'Add a new person to this project (or attach an existing account) and give them a role.',
  },
  EDIT_USER: {
    label: 'Edit users',
    detail: "Change a user's details or their role/access within this project.",
  },
  DELETE_USER: {
    label: 'Remove users',
    detail: 'Remove a person from this project.',
  },
  // 💻 Web — driver management
  ASSIGN_DRIVER: {
    label: 'Assign driver role',
    detail: 'Legacy driver-management permission (not currently used).',
  },
  EDIT_DRIVER_PERMISSIONS: {
    label: 'Edit driver permissions',
    detail: 'Legacy driver-management permission (not currently used).',
  },
  VIEW_DRIVER_ACTIVITY: {
    label: 'View driver activity',
    detail: 'Legacy driver-management permission (not currently used).',
  },
  DELETE_DRIVER: {
    label: 'Remove drivers',
    detail: 'Legacy driver-management permission (not currently used).',
  },
  // 💻 Web — sites / stores
  VIEW_SITES: {
    label: 'View sites / stores',
    detail:
      'Read-only: see the list of stores/sites (collection locations) and their details.',
  },
  CREATE_SITE: {
    label: 'Add sites / stores',
    detail: 'Add a new store/site — a location where collections happen.',
  },
  EDIT_SITE: {
    label: 'Edit sites / stores',
    detail: "Change a store/site's details such as address, zone or contacts.",
  },
  DELETE_SITE: {
    label: 'Delete sites / stores',
    detail: 'Remove a store/site from the project.',
  },
  // 💻 Web — schedules
  VIEW_SITES_SCHEDULE: {
    label: 'View collection schedules',
    detail: 'Read-only: see the collection schedules configured for sites.',
  },
  CREATE_SITE_SCHEDULE: {
    label: 'Add collection schedules',
    detail: 'Set up when collections should happen for a site.',
  },
  EDIT_SITE_SCHEDULE: {
    label: 'Edit collection schedules',
    detail: "Change an existing site's collection schedule.",
  },
  DELETE_SITE_SCHEDULE: {
    label: 'Delete collection schedules',
    detail: 'Remove a collection schedule from a site.',
  },
  // 💻 Web — master data
  VIEW_MASTER_DATA: {
    label: 'View master data',
    detail:
      "Read-only: see the project's reference lists (zones, collection types, etc.).",
  },
  CREATE_MASTER_DATA: {
    label: 'Add master data',
    description: 'Zones, collection types, etc.',
    detail:
      'Add reference data used across the project — zones, collection types and similar lists.',
  },
  EDIT_MASTER_DATA: {
    label: 'Edit master data',
    detail: 'Change existing reference-data entries.',
  },
  DELETE_MASTER_DATA: {
    label: 'Delete master data',
    detail: 'Remove reference-data entries.',
  },
  // 💻 Web — system
  MANAGE_SYSTEM_SETTINGS: {
    label: 'Manage settings',
    detail: 'Change project-level settings and configuration.',
  },
  ADMIN_PORTAL: {
    label: 'Access the web portal',
    description: 'Required to sign in to the web admin portal',
    detail:
      'Required to log into the web admin portal. Give this to any office/admin role — but NOT to drivers (they use the mobile app).',
  },
};

export const permissionLabel = (
  key: string,
  fallback: string,
): { label: string; description?: string; detail?: string } =>
  PERMISSION_LABELS[key] ?? { label: fallback };

// Some permissions are presented as ONE choice even though they map to several
// underlying permissions. "Record collections" (SUBMIT_REPORT) is the driver's
// core capability, so it also grants reporting a failed collection
// (REPORT_ISSUES) and viewing their own history (VIEW_SUBMITTED_REPORT): ticking
// the row grants all of them, and the merged permissions are never shown on
// their own. Keyed by enum KEY name.
export const PERMISSION_MERGES: Record<string, string[]> = {
  SUBMIT_REPORT: ['REPORT_ISSUES', 'VIEW_SUBMITTED_REPORT'],
};

// Enum keys hidden from the picker because they're merged into a primary above.
export const MERGED_AWAY_KEYS = new Set(
  Object.values(PERMISSION_MERGES).flat(),
);

// --- role-config filtering (keyed by the enum KEY name, e.g. "SUBMIT_REPORT",
// which is what GET /permissions returns as `key`) -------------------------

// Shown when configuring a FIELD role (field-only + the shared EDIT_REPORT).
export const FIELD_ROLE_KEYS = new Set([
  'SUBMIT_REPORT',
  'VIEW_SUBMITTED_REPORT',
  'VIEW_BIN_LOCATIONS',
  'REPORT_ISSUES',
  'EDIT_REPORT',
]);

// Hidden from an OFFICE role (pure field actions). EDIT_REPORT is shared so it
// stays visible to office roles too.
export const FIELD_ONLY_KEYS = new Set([
  'SUBMIT_REPORT',
  'VIEW_SUBMITTED_REPORT',
  'VIEW_BIN_LOCATIONS',
  'REPORT_ISSUES',
]);

export const keyInCategory = (key: string, category: RoleCategory): boolean =>
  category === 'field' ? FIELD_ROLE_KEYS.has(key) : !FIELD_ONLY_KEYS.has(key);

// Best-effort category of an existing role from the permission VALUES it holds:
// a role with field actions but no Admin portal is a field/driver role.
export const inferRoleCategory = (
  permissionValues: string[] = [],
): RoleCategory => {
  const hasAdminPortal = permissionValues.includes(Permission.ADMIN_PORTAL);
  const fieldOnlyValues = [
    Permission.SUBMIT_REPORT,
    Permission.VIEW_SUBMITTED_REPORT,
    Permission.VIEW_BIN_LOCATIONS,
    Permission.REPORT_ISSUES,
  ];
  const hasField = fieldOnlyValues.some((v) => permissionValues.includes(v));
  return hasField && !hasAdminPortal ? 'field' : 'office';
};
